import React from 'react';
import PropTypes from 'prop-types';
import i18n from 'i18next';
import { IconButton } from '@mui/material';

import { TablePagination, TableEmptyData } from 'modules/Core/Common';
import {
  WidgetCard,
  WidgetCardHeader,
  WidgetCardSubHeader,
  WidgetCardContent,
  WidgetEmptyData,
} from 'modules/Dashboard/Widgets/Common';
import { formatDatetUTCToTimezone } from 'modules/Utils/Date';
import {
  EProctoringPluginIcon,
  RotateRightIcon,
  SettingsIcon,
} from '@edusynch/edusynch-svg-icons';
import emptyEventsImg from 'assets/layout/empty-data/widgets-empty-data.svg?url';

import * as S from './ExamsInProgress.styles';

const I18N_PREFIX = 'modules.widgets.exams_in_progress';

const formatRelative = (date) => {
  const time = date ? new Date(date).getTime() : NaN;
  if (Number.isNaN(time)) return '-';
  const minutes = Math.max(0, Math.round((Date.now() - time) / 60000));
  const formatter = new Intl.RelativeTimeFormat(i18n.language || 'en', {
    numeric: 'auto',
  });
  if (Math.abs(minutes) < 60) return formatter.format(-minutes, 'minute');
  return formatter.format(-Math.round(minutes / 60), 'hour');
};

const getColumns = ({ userDateFormat, userTimeFormat, userTimezone }) => [
  {
    title: i18n.t(`${I18N_PREFIX}.columns.student`),
    key: 'student',
    sort: false,
    render: ({ student }) => (
      <S.Cell>
        <span>{student?.name}</span>
        <span>{student?.email}</span>
      </S.Cell>
    ),
  },
  {
    title: i18n.t(`${I18N_PREFIX}.columns.test`),
    key: 'test_name',
    sort: false,
    render: ({ test_name: testName }) => <TableEmptyData value={testName} />,
  },
  {
    title: i18n.t(`${I18N_PREFIX}.columns.client`),
    key: 'whitelabel_name',
    sort: false,
    render: ({
      whitelabel_name: whitelabel,
      school_name: school,
      group_name: group,
    }) => (
      <S.Cell>
        <span>{whitelabel || '-'}</span>
        <span>{[school, group].filter(Boolean).join(' / ')}</span>
      </S.Cell>
    ),
  },
  {
    title: i18n.t(`${I18N_PREFIX}.columns.started`),
    key: 'started_at',
    sort: false,
    render: ({ started_at: startedAt }) => (
      <TableEmptyData
        value={formatDatetUTCToTimezone(
          startedAt,
          userDateFormat,
          userTimeFormat,
          userTimezone
        )}
      />
    ),
  },
  {
    title: i18n.t(`${I18N_PREFIX}.columns.last_activity`),
    key: 'last_activity_at',
    sort: false,
    render: ({ last_activity_at: lastActivityAt }) => (
      <TableEmptyData value={formatRelative(lastActivityAt)} />
    ),
  },
];

export const ExamsInProgress = ({
  widget,
  isMock,
  onOpenSettings,
  exams,
  totalItems,
  generatedAt,
  loading,
  hasError,
  hasData,
  authFormat,
  onRefresh,
}) => {
  const size = widget?.settings?.size || 'large';

  const formatUpdatedAt = () =>
    formatDatetUTCToTimezone(
      generatedAt,
      authFormat.userDateFormat,
      authFormat.userTimeFormat,
      authFormat.userTimezone
    );
  const isStale = hasError && hasData;

  const renderResults = () => {
    if (hasError && !hasData) {
      return <S.Error role="alert">{i18n.t(`${I18N_PREFIX}.error`)}</S.Error>;
    }
    if (exams.length === 0) {
      if (hasError) return null;
      return (
        <S.EmptyState>
          <WidgetEmptyData
            imageURL={emptyEventsImg}
            title={i18n.t(`${I18N_PREFIX}.empty_data.title`)}
            subtitle={i18n.t(`${I18N_PREFIX}.empty_data.subtitle`)}
          />
        </S.EmptyState>
      );
    }
    return (
      <TablePagination
        columns={getColumns(authFormat)}
        data={exams.map((exam) => ({ ...exam, id: exam.exercise_id }))}
      />
    );
  };

  return (
    <WidgetCard>
      <WidgetCardHeader
        size={size}
        Icon={EProctoringPluginIcon}
        title={hasError ? '-' : totalItems ?? '-'}
        subtitle={i18n.t(`${I18N_PREFIX}.title`)}
        showSettingsButton={isMock && size === 'small'}
        onOpenSettings={onOpenSettings}
      >
        <WidgetCardSubHeader size={size}>
          <S.ContentAction>
            {isMock && size === 'large' && (
              <IconButton
                aria-label="settings"
                id="settings"
                onClick={onOpenSettings}
              >
                <SettingsIcon />
              </IconButton>
            )}
            {generatedAt && (
              <S.UpdatedAt>
                {i18n.t(`${I18N_PREFIX}.updated_at`)} {formatUpdatedAt()}
              </S.UpdatedAt>
            )}
            {!isMock && (
              <IconButton
                aria-label={i18n.t(`${I18N_PREFIX}.refresh`)}
                id="refresh"
                aria-disabled={loading}
                onClick={loading ? undefined : onRefresh}
              >
                <RotateRightIcon />
              </IconButton>
            )}
          </S.ContentAction>
        </WidgetCardSubHeader>
      </WidgetCardHeader>
      <WidgetCardContent size={size} borderTop>
        <S.Body>
          <S.Results
            data-testid="exams-in-progress-table"
            aria-busy={loading}
            $scrollable={(!hasError || hasData) && exams.length > 0}
          >
            {hasData && (
              <S.Warning role="status" aria-live="polite">
                {isStale &&
                  i18n.t(`${I18N_PREFIX}.stale_warning`, {
                    date: formatUpdatedAt(),
                  })}
              </S.Warning>
            )}
            {renderResults()}
          </S.Results>
        </S.Body>
      </WidgetCardContent>
    </WidgetCard>
  );
};

ExamsInProgress.propTypes = {
  widget: PropTypes.object,
  isMock: PropTypes.bool,
  onOpenSettings: PropTypes.func,
  exams: PropTypes.array,
  totalItems: PropTypes.number,
  generatedAt: PropTypes.string,
  loading: PropTypes.bool,
  hasError: PropTypes.bool,
  hasData: PropTypes.bool,
  authFormat: PropTypes.object.isRequired,
  onRefresh: PropTypes.func,
};
